import { useEffect, useState, useCallback } from "react";
import { Bell, X, CheckCircle2, Info, AlertTriangle, ShieldAlert, Loader2, Send, Plus } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const SEVERITY = {
  info:     { Icon: Info,         color: "#0044FF", bg: "rgba(0,68,255,0.10)"  },
  success:  { Icon: CheckCircle2, color: "#10B981", bg: "rgba(16,185,129,0.10)" },
  warning:  { Icon: AlertTriangle,color: "#F59E0B", bg: "rgba(245,158,11,0.10)" },
  critical: { Icon: ShieldAlert,  color: "#EF4444", bg: "rgba(239,68,68,0.10)"  },
};

export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", severity: "info" });

  const canBroadcast = user?.role === "admin" || user?.role === "master";
  const unread = items.filter(i => !i.is_read).length;

  const load = useCallback(async () => {
    try { const { data } = await api.get("/broadcasts"); setItems(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, [load]);

  const dismiss = async (id) => {
    setItems(items => items.map(i => i.id === id ? { ...i, is_read: true } : i));
    try { await api.post(`/broadcasts/${id}/dismiss`); } catch { /* ignore */ }
  };

  const removeAll = async () => {
    const unreadIds = items.filter(i => !i.is_read).map(i => i.id);
    setItems(items => items.map(i => ({ ...i, is_read: true })));
    try { await Promise.all(unreadIds.map(id => api.post(`/broadcasts/${id}/dismiss`))); } catch { /* ignore */ }
  };

  const send = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body required"); return; }
    setBusy(true);
    try {
      await api.post("/broadcasts", form);
      toast.success("Broadcast sent to all workspace users");
      setForm({ title: "", body: "", severity: "info" });
      setComposeOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid="notification-bell"
            className="relative w-7 h-7 rounded-full bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center shadow-xs transition-transform hover:scale-105"
            title="Notifications"
          >
            <Bell className="w-3.5 h-3.5" />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center bg-red-600 text-white shadow-xs">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-96 p-0" data-testid="notification-panel">
          <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
            <Bell className="w-4 h-4" style={{ color: "var(--brand)" }} />
            <span className="font-semibold text-sm">Notifications</span>
            <span className="text-xs mono ml-auto" style={{ color: "var(--text-soft)" }}>{unread} unread</span>
            {canBroadcast && (
              <Button data-testid="broadcast-open" size="sm" variant="ghost" className="gap-1 h-7" onClick={() => { setOpen(false); setComposeOpen(true); }}>
                <Plus className="w-3.5 h-3.5" /> New
              </Button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-10 text-center text-sm" style={{ color: "var(--text-soft)" }}>
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No announcements yet
              </div>
            ) : (
              items.map(it => {
                const s = SEVERITY[it.severity] || SEVERITY.info;
                const Icon = s.Icon;
                return (
                  <div key={it.id} className={`p-3 border-b flex items-start gap-3 ${it.is_read ? "opacity-70" : ""}`} style={{ borderColor: "var(--border)" }} data-testid={`broadcast-${it.id}`}>
                    <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: s.bg }}>
                      <Icon className="w-4 h-4" style={{ color: s.color }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{it.title}</div>
                      <div className="text-xs mt-0.5" style={{ color: "var(--text-soft)" }}>{it.body}</div>
                      <div className="text-[10px] mono mt-1" style={{ color: "var(--text-soft)" }}>
                        {new Date(it.created_at).toLocaleString()} · {it.scope === "global" ? "Platform" : "Workspace"}
                      </div>
                    </div>
                    {!it.is_read && (
                      <button onClick={() => dismiss(it.id)} className="p-1 hover:bg-slate-100 rounded" data-testid={`dismiss-${it.id}`}>
                        <X className="w-3.5 h-3.5" style={{ color: "var(--text-soft)" }} />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
          {unread > 0 && (
            <div className="p-2 border-t text-center" style={{ borderColor: "var(--border)" }}>
              <Button variant="ghost" size="sm" onClick={removeAll} data-testid="dismiss-all">Mark all as read</Button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      {canBroadcast && (
        <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
          <DialogContent data-testid="broadcast-dialog">
            <DialogHeader>
              <DialogTitle>New announcement</DialogTitle>
              <DialogDescription>
                {user?.role === "master"
                  ? "Master broadcast — visible to EVERY user across ALL workspaces."
                  : "Workspace broadcast — visible to all users in your workspace."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input data-testid="broadcast-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={140} placeholder="Downtime scheduled Sunday" /></div>
              <div><Label>Message</Label><Textarea data-testid="broadcast-body" rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} maxLength={2000} placeholder="Details… (max 2000 chars)" /></div>
              <div>
                <Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                  <SelectTrigger data-testid="broadcast-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">Info (blue)</SelectItem>
                    <SelectItem value="success">Success (green)</SelectItem>
                    <SelectItem value="warning">Warning (amber)</SelectItem>
                    <SelectItem value="critical">Critical (red)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="broadcast-send" onClick={send} disabled={busy} className="text-white gap-2" style={{ background: "var(--brand)" }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send to {user?.role === "master" ? "everyone" : "workspace"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
